import { useEffect, useState } from "react";
import { cn } from "../../lib/utils";
import { api } from "../../api";

type ServiceStatus = "connected" | "disconnected" | "reconnecting" | "error" | "unknown";

interface ServiceInfo {
  service: string;
  status: ServiceStatus;
  lastHeartbeat: string | null;
}

const statusConfig: Record<ServiceStatus, { color: string; pulse: boolean; label: string }> = {
  connected: { color: "bg-emerald-500", pulse: true, label: "Live" },
  disconnected: { color: "bg-red-500", pulse: false, label: "Offline" },
  reconnecting: { color: "bg-amber-500", pulse: true, label: "Reconnecting" },
  error: { color: "bg-red-500", pulse: false, label: "Error" },
  unknown: { color: "bg-gray-500", pulse: false, label: "Unknown" },
};

export function ServiceStatus() {
  const [status, setStatus] = useState<ServiceStatus>("unknown");
  const [services, setServices] = useState<ServiceInfo[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    async function fetchStatus() {
      try {
        const response = await api.status.get();
        if (response.data && "services" in response.data) {
          const serviceList = response.data.services as ServiceInfo[];
          setServices(serviceList);

          // Determine overall status - worst of all services
          const statuses = serviceList.map((s) => s.status);
          if (statuses.includes("error")) {
            setStatus("error");
          } else if (statuses.includes("disconnected")) {
            setStatus("disconnected");
          } else if (statuses.includes("reconnecting")) {
            setStatus("reconnecting");
          } else if (statuses.every((s) => s === "connected")) {
            setStatus("connected");
          } else {
            setStatus("unknown");
          }
        }
      } catch {
        setStatus("unknown");
      }
    }

    // Initial fetch
    fetchStatus();

    // Poll every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const config = statusConfig[status];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-secondary/50 transition-colors"
        title={`Service Status: ${config.label}`}
      >
        <span className="relative flex h-2.5 w-2.5">
          {config.pulse && (
            <span
              className={cn(
                "animate-ping absolute inline-flex h-full w-full rounded-full opacity-75",
                config.color
              )}
            />
          )}
          <span
            className={cn("relative inline-flex rounded-full h-2.5 w-2.5", config.color)}
          />
        </span>
        <span className="text-xs text-muted-foreground">{config.label}</span>
      </button>

      {/* Dropdown with detailed status */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-50 w-64 rounded-lg border border-border bg-card shadow-lg">
            <div className="p-3 border-b border-border">
              <h3 className="text-sm font-semibold">Service Status</h3>
            </div>
            <div className="p-2">
              {services.length === 0 ? (
                <p className="text-xs text-muted-foreground p-2">Loading...</p>
              ) : (
                services.map((service) => {
                  const sConfig = statusConfig[service.status] ?? statusConfig.unknown;
                  return (
                    <div
                      key={service.service}
                      className="flex items-center justify-between p-2 rounded hover:bg-secondary/30"
                    >
                      <div className="flex items-center gap-2">
                        <span className={cn("w-2 h-2 rounded-full", sConfig.color)} />
                        <span className="text-sm">{formatServiceName(service.service)}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{sConfig.label}</span>
                    </div>
                  );
                })
              )}
            </div>
            {services.length > 0 && services[0]?.lastHeartbeat && (
              <div className="px-3 py-2 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Last update: {formatTimestamp(services[0].lastHeartbeat)}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function formatServiceName(service: string): string {
  const names: Record<string, string> = {
    "ably-listener": "Ably Listener",
    "api-ably": "API Subscriber",
  };
  return names[service] ?? service;
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);

  if (diffSecs < 60) return `${diffSecs}s ago`;
  if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;
  return date.toLocaleTimeString();
}
